import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useSearchParams } from 'react-router-dom';

import { BreadcrumbsWrapper, Grid } from '@ha/dsl';
import { useIntl } from 'ha/i18n';

import { HermesMeta } from 'ha/components.legacy/HermesMeta';
import { HermesProps } from 'ha/modules/Hermes';
import { HermesInfo, LinkData } from 'ha/modules/Hermes/types';
import { H1 } from 'ha/ui.legacy/Headers';

import { CommunityCard } from './CommunityCard';
import { CommunityCTACard } from './CommunityCTACard';
import { HermesData } from '../types';
import { CommunityBreadcrumbs } from './CommunityBreadcrumbs';

interface Props {
  linkData: LinkData[];
  info: HermesInfo;
}

const useStyles = makeStyles()(theme => ({
  card: {
    [theme.breakpoints.up('lg')]: {
      flexGrow: 1,
      maxWidth: 'initial',
    },
  },
  headerSubtitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(4),
  },
}));

const CommunityPageLayout: React.FC<Props & HermesProps<HermesData>> = ({
  data: { blogList, header = {}, cta = {}, meta = {} },
  linkData,
  info,
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const [searchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category');

  const filteredBlogList = blogList.filter(item => {
    if (selectedCategory) {
      return item.topicCluster === selectedCategory;
    }

    return true;
  });

  const cardElements = filteredBlogList.map((blogInfo, index) => {
    const kind =
      index === 0
        ? 'primary'
        : index % 6 === 4 || index % 6 === 5
        ? 'featured'
        : 'default';

    return (
      <Grid
        item
        key={blogInfo.pathname}
        className={classes.card}
        xs={12}
        md={kind === 'default' ? 6 : 12}
        lg={kind === 'default' ? 3 : 5}
        data-test-locator="Community Card"
      >
        <CommunityCard
          kind={kind}
          image={blogInfo.headerImage}
          imageAsset={blogInfo.headerImageAsset}
          title={blogInfo.title}
          description={blogInfo.description}
          linkUrl={blogInfo.pathname}
        />
      </Grid>
    );
  });

  const ctaCardElement = (
    <Grid
      item
      key="community-cta-card"
      className={classes.card}
      xs={12}
      md={6}
      lg={3}
      data-test-locator="Community CTA Card"
    >
      <CommunityCTACard {...cta} />
    </Grid>
  );

  return (
    <React.Fragment>
      <HermesMeta
        metaDescription={meta.description || ''}
        metaTitle={meta.title || ''}
        linkData={linkData}
        info={info}
      />
      <BreadcrumbsWrapper>
        <CommunityBreadcrumbs />
      </BreadcrumbsWrapper>
      <div className="m-x-1_5">
        <div className="container-grid">
          <H1
            className="m-t-2_5 m-b-1 color-brand"
            data-test-locator="Community Title"
          >
            {header.heading || T('HousingAnywhere Community')}
          </H1>
          <div
            className={classes.headerSubtitle}
            data-test-locator="Community SubTitle"
          >
            {header.subtitle || T('All you need to survive without moms')}
          </div>
          <Grid container spacing={3}>
            {[
              ...cardElements.slice(0, 2),
              ctaCardElement,
              ...cardElements.slice(2),
            ]}
          </Grid>
        </div>
      </div>
    </React.Fragment>
  );
};

export { CommunityPageLayout };
